// The user's Figma connection: where they can see it, start it and end it.
//
// Account-level, not link-level. `FigmaLinkPanel` offers Connect beside a link
// that needs one; this row, on the account page (`/account`) beside the GitHub
// row, is the one place that says whether Figma is connected at all, and the
// only way to end the connection.
//
// Everything it shows comes from `GET /api/figma/status`:
//
//   - nothing       the route 404s. `server.ts` registers the Figma routes only
//                   with LOOM_FIGMA_ENABLE and both client credentials, so a
//                   404 means Figma is off in this environment
//                   (`onAvailability("off")`). Any other failed read renders
//                   nothing too: a control that cannot say anything true says
//                   nothing. It reports `"failed"`, so the page can say so.
//   - not connected "Connect Figma", a full-page navigation to
//                   `/auth/figma/start` with this page as `returnTo`.
//   - connected     the Figma handle when Figma gave one, and "Disconnect". A
//                   credential Loom can no longer read with (`usable: false`,
//                   the predicate the chips use) says so and offers Reconnect.
//
// DISCONNECT ASKS FIRST. It deletes the credential, every open chat's links
// turn "not connected", and the way back is a Figma login — one click was too
// cheap for that. The first click opens a small card inside the row saying
// exactly that, with Disconnect and Cancel; only the second click writes.
// Focus goes to Cancel, Escape backs out, and focus returns to the button that
// opened it.
//
// A disconnect is honest about its reach. Figma publishes no revocation
// endpoint, so the server deletes Loom's copy and answers
// `revokedAtFigma: false`; the line after it tells the user where Figma itself
// still lists Loom, rather than claiming Loom removed it there.
//
// The chips in the user's open chats move on the server's side
// (`reconcileFigmaLinksForUser`), not from here.
//
// A Connect started HERE comes back HERE, with the callback's `?figma=` marker.
// `FigmaLinkPanel` reads that marker inside a chat, but it is not mounted on
// the account page — so this component reads it too: the outcome line (a
// cancelled or refused connect otherwise leaves no trace) and the marker
// stripped from the URL, so a reload or a copied link does not show an old
// error again.

import { useCallback, useEffect, useId, useRef, useState } from "react";
import { z } from "zod";

import { useT } from "../i18n/index.js";
import { httpGet, httpPost, HttpError } from "../lib/http";
import type { ConnectionAvailability } from "./GitHubConnection";
import {
  FIGMA_RESULT_PARAM,
  figmaConnectHref,
  figmaResultCopyKey,
  figmaReturnToPath,
} from "../focus/figma/figmaCopy";
import { useHereLocation } from "../focus/figma/useHereLocation";

const FigmaStatusSchema = z.object({
  connected: z.boolean(),
  // Optional so an older server that predates the field still renders: it said
  // "connected" before, and that is what it keeps saying.
  usable: z.boolean().optional(),
  handle: z.string().nullable().optional(),
});

const DisconnectSchema = z.object({
  ok: z.boolean(),
  revokedAtFigma: z.boolean().optional(),
});

type View =
  | { readonly kind: "loading" }
  | { readonly kind: "hidden" }
  | { readonly kind: "not_connected" }
  | {
      readonly kind: "connected";
      readonly handle: string | null;
      readonly usable: boolean;
    };

type Note =
  | { readonly kind: "disconnected"; readonly stillListedAtFigma: boolean }
  | { readonly kind: "failed" }
  | null;

export function FigmaConnection({
  location,
  onAvailability,
}: {
  /**
   * Test seam. Production follows the router's history (`useHereLocation`) —
   * never a value frozen at mount, so the `returnTo` is the page the user is on
   * when they click. A handed-in location is never written back.
   */
  readonly location?: { readonly pathname: string; readonly search: string };
  /** Once per settled status read: `"off"` on a 404 (Figma is off here),
   *  `"failed"` on any other error, `"shown"` when the row renders. */
  readonly onAvailability?: (availability: ConnectionAvailability) => void;
}) {
  const t = useT();
  // A ref, so a parent's inline callback neither re-creates `load` nor goes
  // stale inside it.
  const onAvailabilityRef = useRef(onAvailability);
  onAvailabilityRef.current = onAvailability;
  const live = useHereLocation();
  const here = location ?? live.here;
  // Latched, then stripped — the same rule `FigmaLinkPanel` keeps for a Connect
  // started inside a chat.
  const [resultKey, setResultKey] = useState<string | null>(() =>
    figmaResultCopyKey(new URLSearchParams(here.search).get(FIGMA_RESULT_PARAM)),
  );
  const replaceUrl = live.replace;
  const overridden = location !== undefined;
  const hash = overridden ? "" : live.here.hash;
  useEffect(() => {
    const params = new URLSearchParams(here.search);
    if (!params.has(FIGMA_RESULT_PARAM)) return;
    setResultKey(figmaResultCopyKey(params.get(FIGMA_RESULT_PARAM)));
    if (overridden) return;
    replaceUrl(`${figmaReturnToPath(here.pathname, here.search)}${hash}`);
  }, [here.pathname, here.search, hash, overridden, replaceUrl]);
  const [view, setView] = useState<View>({ kind: "loading" });
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<Note>(null);
  const [confirming, setConfirming] = useState(false);
  const alive = useRef(true);
  const busyRef = useRef(false);
  // Every write to `view` takes a new generation, so a status read that
  // started before a disconnect cannot land after it and say "connected".
  const generation = useRef(0);
  const headId = useId();
  const titleId = useId();
  const bodyId = useId();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const connectRef = useRef<HTMLAnchorElement | null>(null);
  // Where focus goes on the next commit. Set by the act that moves it, read
  // once by the effect below — never inferred from state, so a status reload
  // or a mount never steals focus from wherever the user is.
  const focusNext = useRef<
    "cancel" | "trigger" | "close" | "connect" | null
  >(null);

  const load = useCallback(async (): Promise<void> => {
    const mine = ++generation.current;
    let next: View;
    let availability: ConnectionAvailability;
    try {
      const status = await httpGet("/api/figma/status", FigmaStatusSchema);
      next = status.connected
        ? {
            kind: "connected",
            handle: status.handle ?? null,
            usable: status.usable ?? true,
          }
        : { kind: "not_connected" };
      availability = "shown";
    } catch (err) {
      next = { kind: "hidden" };
      availability =
        err instanceof HttpError && err.status === 404 ? "off" : "failed";
    }
    if (alive.current && mine === generation.current) {
      setView(next);
      onAvailabilityRef.current?.(availability);
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    void load();
    return () => {
      alive.current = false;
    };
  }, [load]);

  useEffect(() => {
    const target = focusNext.current;
    if (target === null) return;
    const el =
      target === "cancel"
        ? cancelRef.current
        : target === "trigger"
          ? triggerRef.current
          : target === "connect"
            ? connectRef.current
            : closeRef.current;
    if (el === null) return;
    focusNext.current = null;
    el.focus();
  });

  const askToDisconnect = (): void => {
    // The card replaces any old outcome line: two cards on one spot would say
    // two different things about the same connection.
    setNote(null);
    setResultKey(null);
    focusNext.current = "cancel";
    setConfirming(true);
  };

  const cancelDisconnect = (): void => {
    if (busyRef.current) return;
    focusNext.current = "trigger";
    setConfirming(false);
  };

  const disconnect = async (): Promise<void> => {
    // A ref, not the `busy` state: two clicks inside one render both read the
    // same `busy === false`.
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setNote(null);
    try {
      const res = await httpPost(
        "/auth/figma/disconnect",
        {},
        DisconnectSchema,
      );
      if (alive.current) {
        generation.current += 1;
        setView({ kind: "not_connected" });
        setNote({
          kind: "disconnected",
          stillListedAtFigma: res.revokedAtFigma !== true,
        });
      }
    } catch {
      if (alive.current) setNote({ kind: "failed" });
    } finally {
      busyRef.current = false;
      if (alive.current) {
        setBusy(false);
        // The card has done its job either way; the outcome line takes the
        // spot, and focus with it — the button the user pressed is gone.
        focusNext.current = "close";
        setConfirming(false);
      }
    }
    // Converge from the server either way: a failed response may still have
    // deleted the row, and a done one should be what the server now says.
    await load();
  };

  if (view.kind === "loading" || view.kind === "hidden") return null;

  const connectHref = figmaConnectHref(
    figmaReturnToPath(here.pathname, here.search),
  );

  const sentence =
    view.kind === "not_connected"
      ? t("focus.figma.connection.notConnected")
      : !view.usable
        ? t("focus.figma.connection.lapsed")
        : view.handle !== null
          ? t("focus.figma.connection.connectedAs", { handle: view.handle })
          : t("focus.figma.connection.connected");

  const noteText =
    note?.kind === "disconnected"
      ? note.stillListedAtFigma
        ? `${t("focus.figma.connection.disconnected")} ${t("focus.figma.connection.figmaStillLists")}`
        : t("focus.figma.connection.disconnected")
      : note?.kind === "failed"
        ? t("focus.figma.connection.disconnectFailed")
        : resultKey !== null
          ? t(resultKey)
          : "";

  return (
    <section className="account-row" aria-labelledby={headId}>
      <h3 id={headId} className="account-row-title">
        {t("account.figma.title")}
      </h3>
      <p className="account-row-status">{sentence}</p>
      <div className="account-row-acts">
        {view.kind === "not_connected" && (
          <a ref={connectRef} className="focus-btn" href={connectHref}>
            {t("focus.figma.connect")}
          </a>
        )}
        {view.kind === "connected" && !view.usable && (
          <a ref={connectRef} className="focus-btn" href={connectHref}>
            {t("focus.figma.reconnect")}
          </a>
        )}
        {view.kind === "connected" && !confirming && (
          <button
            ref={triggerRef}
            type="button"
            className="account-act"
            aria-label={t("focus.figma.connection.disconnectLabel")}
            onClick={askToDisconnect}
          >
            {t("focus.figma.connection.disconnect")}
          </button>
        )}
      </div>
      {confirming && (
        <div
          className="account-row-confirm"
          role="group"
          aria-labelledby={titleId}
          aria-describedby={bodyId}
          onKeyDown={(e) => {
            if (e.key !== "Escape") return;
            e.preventDefault();
            cancelDisconnect();
          }}
        >
          <span id={titleId} className="account-row-confirm-title">
            {t("focus.figma.connection.confirm.title")}
          </span>
          <span id={bodyId} className="account-row-confirm-body">
            {t("focus.figma.connection.confirm.body")}
          </span>
          <div className="account-row-confirm-acts">
            <button
              type="button"
              className="focus-btn"
              disabled={busy}
              onClick={() => void disconnect()}
            >
              {busy
                ? t("focus.figma.connection.disconnecting")
                : t("focus.figma.connection.disconnect")}
            </button>
            <button
              ref={cancelRef}
              type="button"
              className="focus-btn is-ghost"
              disabled={busy}
              onClick={cancelDisconnect}
            >
              {t("common.cancel")}
            </button>
          </div>
        </div>
      )}
      {/* Always mounted, so a screen reader announces the line when it fills. */}
      <div
        className={"account-row-note" + (noteText === "" ? " is-empty" : "")}
      >
        <p className="account-row-note-text" role="status">
          {noteText}
        </p>
        {noteText !== "" && (
          <button
            ref={closeRef}
            type="button"
            className="account-act"
            onClick={() => {
              // Close unmounts itself. Focus goes to what the line was about —
              // Connect after a disconnect, Disconnect when the connection
              // stands — never down to the page body.
              focusNext.current =
                view.kind === "connected" ? "trigger" : "connect";
              setNote(null);
              setResultKey(null);
            }}
          >
            {t("common.close")}
          </button>
        )}
      </div>
    </section>
  );
}
